import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { FaTimes, FaExclamationTriangle, FaPlus } from 'react-icons/fa';
import { api } from '../../services/api';
import * as Sentry from "@sentry/react";
import { useAuth } from '../../components/auth/AuthContext';

// ============================================================================
// SENTRY LOGGING HELPERS
// ============================================================================
const logError = (message, context = {}, error = null) => {
  if (error) {
    Sentry.captureException(error, { 
      tags: { section: 'firewall-create-modal' },
      extra: { ...context, message }
    });
  } else {
    Sentry.captureMessage(message, { 
      level: 'error',
      tags: { section: 'firewall-create-modal' },
      extra: context 
    });
  }
};

const logInfo = (message, context = {}) => {
  Sentry.addBreadcrumb({
    message,
    category: 'firewall-create-modal',
    level: 'info',
    data: context
  });
};

const logUserAction = (action, context = {}) => {
  Sentry.addBreadcrumb({
    message: `User action: ${action}`,
    category: 'user-interaction',
    level: 'info',
    data: context
  });
};

const logPerformance = (operation, duration, context = {}) => {
  Sentry.addBreadcrumb({
    message: `Performance: ${operation} took ${duration}ms`,
    category: 'performance',
    level: 'info',
    data: { ...context, duration }
  });
};

// ============================================================================
// ERROR MESSAGES MAPPING
// ============================================================================
const ERROR_MESSAGES = {
  'AUTH_ERROR': 'Please log in again to continue',
  'USER_NOT_FOUND': 'User profile not found. Please refresh and try again',
  'ACCESS_DENIED': 'You do not have permission to perform this action',
  'RATE_LIMIT_EXCEEDED': 'API limit reached. Try again after 60 seconds',
  'INVALID_USER_PROFILE': 'Invalid user profile format. Please contact support',
  'INCOMPLETE_PROFILE': 'Please complete your profile to continue',
  'SERVICE_CONFIG_ERROR': 'Service temporarily unavailable. Please try again later',
  'DB_ERROR': 'Service temporarily unavailable. Please try again later',
  'API_ERROR': 'Service temporarily unavailable. Please try again later',
  'INVALID_API_RESPONSE': 'Service error. Please try again later',
  'API_CONNECTION_ERROR': 'Unable to connect to service. Please check your connection and try again',
  'NETWORK_ERROR': 'Unable to connect to service. Please check your connection and try again',
  'INVALID_ID_FORMAT': 'Invalid request format. Please try again',
  'INVALID_DATA': 'Invalid input data. Please check your entries and try again',
  'INVALID_REQUEST': 'Invalid request format. Please try again',
  'MISSING_FIELDS': 'Please fill in all required fields',
  'VALIDATION_ERROR': 'Please check your input and try again',
  'INVALID_RULE_PARAMETERS': 'Invalid firewall rule parameters. Please check your input',
  'INVALID_JSON': 'Invalid request format. Please check your input',
  'UNEXPECTED_PAYLOAD': 'Unexpected request data. Please try again',
  'UNEXPECTED_BODY': 'Invalid request format. Please try again',
  'INVALID_RULE_ID_FORMAT': 'Invalid rule ID format. Please try again',
  'FIREWALL_LIST_FAILED': 'Unable to load firewalls. Please refresh the page',
  'FIREWALL_DETAILS_FAILED': 'Unable to load firewall details. Please try again',
  'FIREWALL_CREATE_FAILED': 'Unable to create firewall. Please try again',
  'FIREWALL_DELETE_FAILED': 'Unable to delete firewall. Please try again',
  'FIREWALL_NOT_FOUND': 'Firewall not found. It may have been deleted',
  'FIREWALL_LIMIT_EXCEEDED': 'You have reached the maximum limit of 50 firewalls. Delete unused firewalls to create new ones',
  'FIREWALL_HAS_ATTACHMENTS': 'Firewall is attached to VMs. Please detach before deleting',
  'FIREWALL_IN_USE': 'Firewall is currently in use. Please detach all instances before deleting',
  'RESOURCE_ALREADY_EXISTS': 'A firewall with this name already exists. Please choose a different name',
  'FIREWALL_RULE_ADD_FAILED': 'Unable to add firewall rule. Please try again',
  'FIREWALL_RULE_DELETE_FAILED': 'Unable to remove firewall rule. Please try again',
  'FIREWALL_RULE_NOT_FOUND': 'Firewall rule not found. It may have been removed',
  'SECURITY_RULE_ADD_FAILED': 'Unable to add security rule. Please try again',
  'SECURITY_RULE_DELETE_FAILED': 'Unable to remove security rule. Please try again',
  'SECURITY_RULE_ALREADY_EXISTS': 'This security rule already exists for this instance',
  'RULE_NOT_FOUND': 'Security rule not found. It may have been removed',
  'VM_LIST_FAILED': 'Unable to load virtual machines. Please refresh the page',
  'VM_NOT_FOUND': 'Virtual machine not found. It may have been deleted',
  'VM_NOT_ACTIVE': 'Virtual machine must be active to perform this action',
  'VM_FIREWALLS_FAILED': 'Unable to load VM firewall information. Please try again',
  'VM_EVENTS_FAILED': 'Unable to load VM events. Please try again',
  'FIREWALL_ATTACH_FAILED': 'Unable to attach firewall to VMs. Please try again',
  'NO_VMS_SELECTED': 'Please select at least one virtual machine',
  'INVALID_VM_IDS': 'Some selected VMs are invalid or not found',
  'VM_NOT_COMPATIBLE': 'Selected VMs are not compatible with this firewall (different regions)',
  'INSTANCE_NOT_FOUND': 'Instance not found. It may have been deleted',
  'INSTANCE_NOT_AVAILABLE': 'Instance is not available for this operation',
  'INSTANCE_NOT_COMPATIBLE': 'Selected instances are not compatible with this firewall',
  'INSTANCES_NOT_ACTIVE': 'Some instances are not active. Only active instances can be modified',
  'INSTANCE_NOT_ACTIVE': 'Instance must be active to perform this action',
  'INVALID_INSTANCE_ID_FORMAT': 'Invalid instance ID format. Expected format: BRK-XXXXXXD',
  'TIER_NOT_SUPPORTED': 'This operation is only supported for legacy tier instances',
  'REGION_NOT_AVAILABLE': 'Selected region is not available. Please choose a different region',
  'INVALID_REGION_FORMAT': 'Invalid region format. Please try again',
  'UNSUPPORTED_REGION': 'This region is not supported for firewalls',
  'PROTOCOLS_FAILED': 'Unable to load supported protocols. Please refresh the page',
  'REGIONS_FAILED': 'Unable to load available regions. Please refresh the page',
  // Network Errors (NEW)
'OFFLINE': "You're offline. Check your connection.",
'TIMEOUT': "Request timed out. Please try again.",
'SERVER_UNREACHABLE': "Network connection failed. Please check your internet connection and try again.",
'NO_RESPONSE': "No response from server. Please try again.",
'UNKNOWN_NETWORK_ERROR': "Something went wrong. Please try again.",
'TOO_MANY_REQUESTS': "Too many requests. Please wait before trying again.",
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================
const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/[<>&"']/g, (char) => {
      const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
      return entities[char];
    })
    .substring(0, 1000);
};

const validateFirewallName = (name) => {
  if (!name || typeof name !== 'string') return { valid: false, error: 'Name is required' };
  
  const trimmedName = name.trim();
  
  if (trimmedName.length < 3) return { valid: false, error: 'Firewall name must be at least 3 characters' };
  if (trimmedName.length > 30) return { valid: false, error: 'Firewall name cannot exceed 30 characters' };
  if (!/^[a-zA-Z0-9\-_\.]+$/.test(trimmedName)) return { valid: false, error: 'Use only letters, numbers, spaces, hyphens, underscores, and periods' };
  if (trimmedName.startsWith('-') || trimmedName.startsWith('_')) return { valid: false, error: 'Cannot start with a hyphen or underscore' };
  if (trimmedName.endsWith('-') || trimmedName.endsWith('_')) return { valid: false, error: 'Cannot end with a hyphen or underscore' };
  
  return { valid: true };
};

const validateFirewallDescription = (description) => {
  if (description && typeof description === 'string' && description.length > 255) {
    return { valid: false, error: 'Description must be 255 characters or less' };
  }
  return { valid: true };
};

const getSafeErrorMessage = (error) => {
  if (!error || typeof error !== 'object') {
    return 'Something went wrong. Please try again.';
  }
  
  if (error.userMessage) {
    return error.userMessage;
  }
  
  if (error.error) {
    return error.error;
  }
  
  if (error.response?.data?.code && ERROR_MESSAGES[error.response.data.code]) {
    return ERROR_MESSAGES[error.response.data.code];
  }
  
  if (error.code && ERROR_MESSAGES[error.code]) {
    return ERROR_MESSAGES[error.code];
  }
  
  if (error.response?.status === 401 || error.status === 401) {
    return ERROR_MESSAGES['AUTH_ERROR'];
  }
  
  return 'Something went wrong. Please try again.';
};
// Network error detection with 6-position priority
const getNetworkErrorMessage = (error) => {
  // Position 2.1: Browser knows it's offline
  if (!navigator.onLine) {
    return {
      message: "You're offline. Check your connection.",
      code: 'OFFLINE'
    };
  }
  
  // Position 2.2: Request timeout
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
    return {
      message: "Request timed out. Please try again.",
      code: 'TIMEOUT'
    };
  }
  
  // Position 2.3: Network error but browser thinks it's online
  if (error.code === 'ERR_NETWORK' && navigator.onLine) {
    return {
      message: "Unable to reach server. Please try later.",
      code: 'SERVER_UNREACHABLE'
    };
  }
  
  // Position 2.4: Network error and browser now offline
  if (error.code === 'ERR_NETWORK' && !navigator.onLine) {
    return {
      message: "You're offline. Check your connection.",
      code: 'OFFLINE'
    };
  }
  
  // Position 2.5: Request sent but no response received
  if (error.request && !error.response) {
    return {
      message: "No response from server. Please try again.",
      code: 'NO_RESPONSE'
    };
  }
  
  // Position 2.6: Fallback
  return {
    message: "Something went wrong. Please try again.",
    code: 'UNKNOWN_NETWORK_ERROR'
  };
};

const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

// ============================================================================
// RESPONSIVE HOOK
// ============================================================================
const useResponsive = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  return { isMobile, isTablet };
};

// ============================================================================
// INLINE STYLES
// ============================================================================
const getStyles = (isSubmitting) => ({
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
    padding: '16px',
  },
  modal: {
    backgroundColor: '#121212',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '620px',
    border: '1px solid #2a2a2a',
    overflow: 'hidden',
    display: 'flex',
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
  },
  leftPanel: {
    flex: 1,
    padding: '28px',
    borderRight: '1px solid #2a2a2a',
    opacity: isSubmitting ? 0.6 : 1,
    transition: 'opacity 0.15s',
    pointerEvents: isSubmitting ? 'none' : 'auto',
  },
  rightPanel: {
    width: '220px',
    padding: '28px',
    backgroundColor: '#0a0a0a',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '28px',
  },
  iconWrapper: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    background: isSubmitting 
      ? 'linear-gradient(135deg, #3a3a3a 0%, #2a2a2a 100%)'
      : 'linear-gradient(135deg, #e0eacc 0%, #b8d4a0 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '14px',
    boxShadow: isSubmitting ? 'none' : '0 4px 12px rgba(224,234,204,0.3)',
    transition: 'all 0.15s',
  },
  title: {
    color: 'white',
    margin: 0,
    fontSize: '20px',
    fontWeight: '600',
  },
  subtitle: {
    color: '#6b7280',
    margin: 0,
    fontSize: '13px',
  },
  label: {
    color: '#9ca3af',
    fontSize: '12px',
    fontWeight: '500',
    marginBottom: '6px',
    display: 'block',
  },
  required: {
    color: '#ff6b6b',
    marginLeft: '2px',
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    backgroundColor: isSubmitting ? '#151515' : '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '8px',
    color: isSubmitting ? '#666' : 'white',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'all 0.15s',
  },
  textarea: {
    width: '100%',
    padding: '12px 14px',
    backgroundColor: isSubmitting ? '#151515' : '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '8px',
    color: isSubmitting ? '#666' : 'white',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'all 0.15s',
    resize: 'none',
    minHeight: '80px',
    fontFamily: 'inherit',
  },
  dropdown: {
    position: 'relative',
  },
  dropdownTrigger: {
    width: '100%',
    padding: '12px 14px',
    backgroundColor: isSubmitting ? '#151515' : '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '8px',
    color: isSubmitting ? '#666' : 'white',
    fontSize: '14px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: isSubmitting ? 'not-allowed' : 'pointer',
    boxSizing: 'border-box',
    transition: 'all 0.15s',
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: '4px',
    backgroundColor: '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '8px',
    zIndex: 100,
    maxHeight: '160px',
    overflow: 'auto',
  },
  dropdownItem: {
    padding: '10px 14px',
    color: 'white',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  errorBox: {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: '8px',
    padding: '12px 14px',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
  },
  errorText: {
    color: '#ff6b6b',
    fontSize: '11px',
    marginTop: '4px',
  },
  submitBtn: {
    width: '100%',
    padding: '14px 24px',
    backgroundColor: isSubmitting ? '#3d4435' : '#e0eacc',
    color: isSubmitting ? '#a0a89a' : 'black',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: isSubmitting ? 'not-allowed' : 'pointer',
    transition: 'all 0.15s',
    boxShadow: isSubmitting ? 'none' : '0 4px 14px rgba(224,234,204,0.25)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '48px',
  },
  cancelBtn: {
    width: '100%',
    marginTop: '10px',
    padding: '12px 24px',
    backgroundColor: '#333',
    color: isSubmitting ? '#666' : 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: isSubmitting ? 'not-allowed' : 'pointer',
    opacity: isSubmitting ? 0.5 : 1,
  },
  spinner: {
    width: '16px',
    height: '16px',
    border: '2px solid rgba(0,0,0,0.2)',
    borderTopColor: 'black',
    borderRadius: '50%',
    animation: 'spin 0.7s linear infinite',
  },
  summaryLabel: {
    color: '#6b7280',
    fontSize: '12px',
    margin: '0 0 4px 0',
  },
  summaryValue: {
    color: 'white',
    fontSize: '15px',
    margin: 0,
    wordBreak: 'break-all',
  },
});

// ============================================================================
// SHIELD ICON COMPONENT
// ============================================================================
const ShieldIcon = ({ size = 20, color = '#1a1a1a' }) => (
  <svg width={size} height={size + 4} viewBox="0 0 24 28" fill={color}>
    <path d="M12 0L0 4v10c0 6 5 12 12 14 7-2 12-8 12-14V4L12 0z"/>
  </svg>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const FirewallCreateModal = ({ isOpen, onClose, environments, onFirewallCreated, existingFirewallNames = [] }) => {
  const { handleAuthError } = useAuth();
  const { isMobile, isTablet } = useResponsive();
  
  // Form states
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [isRegionDropdownOpen, setIsRegionDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [availableRegions, setAvailableRegions] = useState([]);
  const [fetchingRegions, setFetchingRegions] = useState(true);
  
  // Validation states
  const [nameError, setNameError] = useState('');
  const [descriptionError, setDescriptionError] = useState('');
  const [nameCharCount, setNameCharCount] = useState(0);
  const [descriptionCharCount, setDescriptionCharCount] = useState(0);
  
  // Hover state for description tooltip
  const [isDescriptionHovered, setIsDescriptionHovered] = useState(false);
  
  // Refs
  const dropdownRef = useRef(null);
  const abortControllerRef = useRef(null);
  
  const maxRetries = 3;
  const retryAttempts = useRef(0);
  
  const API_BASE_URL = 'https://barrack.ai';
  
  // Derived values
  const styles = getStyles(loading);
  const isFormValid = name.trim() && selectedRegion && !nameError && !descriptionError;
  const isMobileOrTablet = isMobile || isTablet;
  
  // Sentry context
  useEffect(() => {
    if (isOpen) {
      Sentry.setContext("firewall-create-modal", {
        firewallName: name,
        selectedRegion,
        availableRegionCount: availableRegions.length,
        nameLength: name.length,
        descriptionLength: description.length,
        hasValidationErrors: !!(nameError || descriptionError),
        isLoading: loading,
        isFetchingRegions: fetchingRegions,
        isMobile,
        isTablet
      });
    }
  }, [isOpen, name, selectedRegion, availableRegions.length, description.length, nameError, descriptionError, loading, fetchingRegions, isMobile, isTablet]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        logInfo('Cleanup: Aborted pending firewall create modal requests');
      }
    };
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsRegionDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch regions
  const debouncedFetchRegions = useCallback(
    debounce(async () => {
      const startTime = performance.now();
      
      logInfo('Initiating regions fetch for firewall creation');
      
      if (retryAttempts.current >= maxRetries) {
        setError('Maximum refresh attempts reached. Please wait before trying again.');
        logError('Maximum regions fetch attempts exceeded for firewall creation', {
          attempts: retryAttempts.current,
          maxRetries
        });
        return;
      }
      
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      abortControllerRef.current = new AbortController();
      
      retryAttempts.current++;
      setFetchingRegions(true);
      
      try {
        const response = await axios.get(`${API_BASE_URL}/api/regions/`, {
          withCredentials: true,
          signal: abortControllerRef.current.signal
        });
        
        const endTime = performance.now();
        const duration = endTime - startTime;

        if (abortControllerRef.current?.signal.aborted) {
          logInfo('Regions fetch request was aborted', { duration });
          return;
        }
        
        if (response.data.success && response.data.regions) {
          const allRegions = response.data.regions;
          const regions = allRegions.filter(region => region.tier === 'legacy');
          
          setAvailableRegions(regions);
          if (regions.length > 0 && !selectedRegion) {
            setSelectedRegion(regions[0].id);
            logInfo('Auto-selected first available region', {
              regionId: regions[0].id,
              regionName: regions[0].name
            });
          }
          retryAttempts.current = 0;

          logPerformance('fetch_regions_for_firewall', duration, {
            regionCount: regions.length,
            totalRegionCount: allRegions.length,
            success: true
          });

          logInfo('Regions loaded successfully for firewall creation', {
            count: regions.length,
            totalCount: allRegions.length,
            duration: `${duration.toFixed(2)}ms`
          });
        } else {
          const safeMessage = getSafeErrorMessage(response.data);
          setError(safeMessage);
          
          logError('Failed to fetch regions for firewall creation', {
            response: response.data || 'No response data',
            duration: `${duration.toFixed(2)}ms`
          });
        }
   } catch (err) {
  if (err.name === 'AbortError') {
    logInfo('Regions fetch request aborted by user');
    return;
  }
  
  const endTime = performance.now();
  const duration = endTime - startTime;
  
  // Position 1: Authentication error
  if (err.response?.status === 401 || err.status === 401) {
    handleAuthError();
    setFetchingRegions(false);
    return;
  }
  
  // Position 2: Network error (no response)
  if (!err.response) {
    const networkError = getNetworkErrorMessage(err);
    setError(networkError.message);
    logError('Network error in fetchRegions', { 
      code: networkError.code, 
      originalCode: err.code,
      online: navigator.onLine,
      duration: `${duration.toFixed(2)}ms`
    }, err);
    setFetchingRegions(false);
    return;
  }
  
  // Position 3: Rate limit
  if (err.response?.status === 429) {
    const retryAfter = err.response?.headers?.['retry-after'] || 60;
    setError(`Too many requests. Please wait ${retryAfter} seconds.`);
    setFetchingRegions(false);
    return;
  }
  
  // Position 4: Backend error code
  if (err.response?.data?.code && ERROR_MESSAGES[err.response.data.code]) {
    setError(ERROR_MESSAGES[err.response.data.code]);
    setFetchingRegions(false);
    return;
  }
  
  // Position 5: Direct error code
  if (err.code && ERROR_MESSAGES[err.code]) {
    setError(ERROR_MESSAGES[err.code]);
    logError('Regions fetch error with code', {
      code: err.code,
      duration: `${duration.toFixed(2)}ms`
    }, err);
    setFetchingRegions(false);
    return;
  }
  
  // Position 6: Fallback
  const safeMessage = getSafeErrorMessage(err);
  setError(safeMessage);
  
  logError('Error fetching regions for firewall creation', {
    errorName: err.name,
    errorMessage: err.message,
    duration: `${duration.toFixed(2)}ms`
  }, err);
} finally {
  setFetchingRegions(false);
}
    }, 300),
    [selectedRegion, API_BASE_URL, handleAuthError]
  );

  // Reset form
  const resetForm = useCallback(() => {
    logInfo('Firewall create form reset');
    setName('');
    setDescription('');
    setNameError('');
    setDescriptionError('');
    setNameCharCount(0);
    setDescriptionCharCount(0);
    setError(null);
    setIsDescriptionHovered(false);
  }, []);

  // Initialize on modal open
  useEffect(() => {
    if (isOpen) {
      logInfo('Firewall create modal opened', { isMobile, isTablet });
      logUserAction('firewall_create_modal_opened');
      debouncedFetchRegions();
      resetForm();
    }
  }, [isOpen, debouncedFetchRegions, resetForm, isMobile, isTablet]);

  // Handlers
  const handleNameChange = useCallback((e) => {
    const value = e.target.value;
    setName(value);
    setNameCharCount(value.length);
    setError(null);
    
    logUserAction('firewall_name_changed', {
      nameLength: value.length,
      hasError: false
    });
    
    const validation = validateFirewallName(value.trim());
    if (!validation.valid) {
      setNameError(validation.error);
      logInfo('Firewall name validation failed', {
        error: validation.error,
        nameLength: value.length
      });
    } else if (existingFirewallNames.includes(value.trim())) {
      setNameError('A firewall with this name already exists');
      logInfo('Firewall name validation failed - duplicate name', {
        attemptedName: value.trim(),
        existingNames: existingFirewallNames.length
      });
    } else {
      setNameError('');
      logInfo('Firewall name validation passed', {
        nameLength: value.length
      });
    }
  }, [existingFirewallNames]);

  const handleDescriptionChange = useCallback((e) => {
    const value = e.target.value;
    setDescription(value);
    setDescriptionCharCount(value.length);
    
    logUserAction('firewall_description_changed', {
      descriptionLength: value.length
    });
    
    const validation = validateFirewallDescription(value);
    if (!validation.valid) {
      setDescriptionError(validation.error);
      logInfo('Firewall description validation failed', {
        error: validation.error,
        descriptionLength: value.length
      });
    } else {
      setDescriptionError('');
      logInfo('Firewall description validation passed', {
        descriptionLength: value.length
      });
    }
  }, []);

  const handleRegionSelect = useCallback((regionId, regionName) => {
    logUserAction('region_selected_for_firewall', {
      regionId,
      regionName,
      previousRegion: selectedRegion
    });
    
    logInfo('Region selected for firewall creation', {
      regionId,
      regionName,
      previousRegion: selectedRegion
    });
    
    setSelectedRegion(regionId);
    setIsRegionDropdownOpen(false);
    setError(null);
  }, [selectedRegion]);

const getSelectedRegionName = useCallback(() => {
  if (!selectedRegion || !availableRegions.length) return 'Select a region';
  const region = availableRegions.find(r => r.id === selectedRegion);
  return region ? sanitizeText(region.id) : 'Select a region';  // Changed from region.name to region.id
}, [selectedRegion, availableRegions]);

const handleSubmit = useCallback(async () => {
  const startTime = performance.now();
  
  logUserAction('firewall_creation_submitted', {
    firewallName: name,
    selectedRegion,
    hasDescription: !!description.trim()
  });
  
  logInfo('Starting firewall creation process', {
    firewallName: name,
    selectedRegion,
    nameLength: name.length,
    descriptionLength: description.length
  });
  
  const nameValidation = validateFirewallName(name.trim());
  const descriptionValidation = validateFirewallDescription(description.trim());

  if (!nameValidation.valid) {
    setNameError(nameValidation.error);
    setError(nameValidation.error);
    logError('Firewall creation failed - name validation error', {
      error: nameValidation.error,
      firewallName: name
    });
    return;
  }

  if (existingFirewallNames.includes(sanitizeText(name.trim()))) {
    setNameError('A firewall with this name already exists');
    setError('A firewall with this name already exists');
    logError('Firewall creation failed - duplicate name', {
      firewallName: name.trim(),
      existingNames: existingFirewallNames.length
    });
    return;
  }
  
  if (!descriptionValidation.valid) {
    setDescriptionError(descriptionValidation.error);
    setError(descriptionValidation.error);
    logError('Firewall creation failed - description validation error', {
      error: descriptionValidation.error,
      descriptionLength: description.length
    });
    return;
  }
  
  if (!selectedRegion) {
    const safeMessage = getSafeErrorMessage({ code: 'REGION_NOT_AVAILABLE' });
    setError(safeMessage);
    logError('Firewall creation failed - no region selected', {
      availableRegionCount: availableRegions.length
    });
    return;
  }
  
  if (abortControllerRef.current) {
    abortControllerRef.current.abort();
  }
  
  abortControllerRef.current = new AbortController();
  
  setLoading(true);
  setError(null);
  
  try {
    const response = await api.createFirewall({
      name: sanitizeText(name.trim()),
      description: sanitizeText(description.trim()) || undefined,
      region: sanitizeText(selectedRegion)
    });
    
    const endTime = performance.now();
    const duration = endTime - startTime;

    if (abortControllerRef.current?.signal.aborted) {
      logInfo('Firewall creation request was aborted', { duration });
      return;
    }
    
    if (response.success) {
      onFirewallCreated(response.firewall);
      onClose();

      logPerformance('create_firewall', duration, {
        firewallName: name,
        selectedRegion,
        hasDescription: !!description.trim(),
        success: true
      });

      logInfo('Firewall created successfully', {
        firewallId: response.firewall?.id,
        firewallName: name,
        selectedRegion,
        duration: `${duration.toFixed(2)}ms`
      });

      logUserAction('firewall_creation_success', {
        firewallId: response.firewall?.id,
        firewallName: name,
        selectedRegion
      });
    } else {
      const safeMessage = getSafeErrorMessage(response);
      setError(safeMessage);
      
      logError('Failed to create firewall', {
        firewallName: name,
        selectedRegion,
        code: response?.code,
        duration: `${duration.toFixed(2)}ms`
      });

      logUserAction('firewall_creation_failed', {
        firewallName: name,
        selectedRegion,
        reason: 'api_error'
      });
    }
  } catch (err) {
    if (err.name === 'AbortError') {
      logInfo('Firewall creation request aborted by user');
      return;
    }
    
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Position 1: Authentication error
    if (err.status === 401 || err.code === 'AUTH_ERROR') {
      logError('Authentication error during firewall creation', {
        firewallName: name,
        selectedRegion,
        code: err.code,
        duration: `${duration.toFixed(2)}ms`
      });
      handleAuthError();
      return;
    }
    
    // Position 2: Use ERROR_MESSAGES lookup if code exists and has mapping
    if (err.code && ERROR_MESSAGES[err.code]) {
      setError(ERROR_MESSAGES[err.code]);
      logError('Firewall creation error', {
        firewallName: name,
        selectedRegion,
        code: err.code,
        status: err.status,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      return;
    }
    
    // Position 3: Use interceptor's user-friendly message directly
    if (err.error) {
      setError(err.error);
      logError('Firewall creation error', {
        firewallName: name,
        selectedRegion,
        code: err.code,
        status: err.status,
        message: err.error,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      return;
    }
    
    // Position 4: Fallback for unexpected error format
    setError('Something went wrong. Please try again.');
    logError('Firewall creation error (unexpected format)', {
      firewallName: name,
      selectedRegion,
      errorType: typeof err,
      duration: `${duration.toFixed(2)}ms`
    }, err);

    logUserAction('firewall_creation_error', {
      firewallName: name,
      selectedRegion,
      reason: 'exception'
    });
  } finally {
    setLoading(false);
  }     
}, [
  name,
  description,
  selectedRegion,
  availableRegions.length,
  onFirewallCreated,
  onClose,
  existingFirewallNames,
  handleAuthError
]);

const handleClose = useCallback(() => {
    if (loading) return;
    
    logUserAction('firewall_create_modal_closed', {
      firewallName: name,
      selectedRegion,
      hadInput: !!(name || description)
    });
    
    logInfo('Firewall create modal closed', {
      firewallName: name,
      selectedRegion,
      nameLength: name.length,
      descriptionLength: description.length
    });
    
    onClose();
  }, [name, description, selectedRegion, onClose, loading]);

  // Get modal styles based on device
  const getModalStyles = useCallback(() => {
    if (isMobile) {
      return {
        width: 'calc(100% - 1rem)',
        maxWidth: '100%',
        maxHeight: '90vh',
        margin: '0.5rem',
        flexDirection: 'column',
      };
    } else if (isTablet) {
      return {
        width: 'calc(100% - 2rem)',
        maxWidth: '500px',
        maxHeight: '85vh',
        margin: '1rem',
        flexDirection: 'column',
      };
    }
    return {};
  }, [isMobile, isTablet]);

  if (!isOpen) return null;

  const modalStyles = getModalStyles();

  return (
    <>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .firewall-modal-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .firewall-modal-scrollbar::-webkit-scrollbar-track {
          background: #2a2a2a;
          border-radius: 3px;
        }
        .firewall-modal-scrollbar::-webkit-scrollbar-thumb {
          background: #555;
          border-radius: 3px;
        }
        .firewall-modal-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #666;
        }
      `}</style>
      
      <div style={styles.overlay}>
        <div style={{ ...styles.modal, ...modalStyles }}>
          {/* Mobile/Tablet Header with Close Button */}
          {isMobileOrTablet && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px',
              borderBottom: '1px solid #2a2a2a',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #e0eacc 0%, #b8d4a0 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <ShieldIcon size={18} color="#1a1a1a" />
                </div>
                <div>
                  <h2 style={{ color: 'white', margin: 0, fontSize: '18px', fontWeight: '600' }}>Create Firewall</h2>
                  <p style={{ color: '#6b7280', margin: 0, fontSize: '12px' }}>Configure network security</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                disabled={loading}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#6b7280',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  padding: '8px',
                  opacity: loading ? 0.5 : 1,
                }}
              >
                <FaTimes size={20} />
              </button>
            </div>
          )}

          {/* LEFT PANEL - Configuration */}
          <div style={{
            ...styles.leftPanel,
            borderRight: isMobileOrTablet ? 'none' : '1px solid #2a2a2a',
            padding: isMobileOrTablet ? '20px' : '28px',
            overflowY: isMobileOrTablet ? 'auto' : 'visible',
            maxHeight: isMobileOrTablet ? 'calc(90vh - 80px)' : 'none',
          }}>
            {/* Desktop Header */}
            {!isMobileOrTablet && (
              <div style={styles.header}>
                <div style={styles.iconWrapper}>
                  <ShieldIcon size={22} color={loading ? '#666' : '#1a1a1a'} />
                </div>
                <div>
                  <h2 style={styles.title}>Create Firewall</h2>
                  <p style={styles.subtitle}>Configure network security</p>
                </div>
              </div>
            )}

            {/* Error Display */}
            {error && (
              <div style={styles.errorBox}>
                <FaExclamationTriangle style={{ color: '#ef4444', marginTop: '2px', flexShrink: 0 }} />
                <span style={{ color: '#fca5a5', fontSize: '13px' }}>{sanitizeText(error)}</span>
              </div>
            )}

            {/* Name Field */}
            <div style={{ marginBottom: '18px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <label style={styles.label}>
                  Firewall Name<span style={styles.required}>*</span>
                </label>
                <span style={{ fontSize: '11px', color: nameCharCount > 25 ? '#ff6b6b' : '#6b7280' }}>
                  {nameCharCount}/30
                </span>
              </div>
              <input
                type="text"
                value={name}
                onChange={handleNameChange}
                placeholder="Enter firewall name"
                disabled={loading}
                maxLength={30}
                style={{
                  ...styles.input,
                  borderColor: nameError ? '#ff6b6b' : '#333',
                }}
                onFocus={(e) => {
                  if (!loading) {
                    e.target.style.borderColor = '#e0eacc';
                    e.target.style.backgroundColor = '#252525';
                    e.target.style.boxShadow = '0 0 0 3px rgba(224, 234, 204, 0.15)';
                  }
                  logUserAction('firewall_name_field_focused');
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = nameError ? '#ff6b6b' : '#333';
                  e.target.style.backgroundColor = '#1a1a1a';
                  e.target.style.boxShadow = 'none';
                  logUserAction('firewall_name_field_blurred', {
                    nameLength: name.length,
                    hasError: !!nameError
                  });
                }}
              />
              {nameError && <div style={styles.errorText}>{sanitizeText(nameError)}</div>}
            </div>

            {/* Region Dropdown */}
            <div style={{ ...styles.dropdown, marginBottom: '18px' }} ref={dropdownRef}>
              <label style={styles.label}>
                Region<span style={styles.required}>*</span>
              </label>
              
              {fetchingRegions ? (
                <div style={{
                  ...styles.dropdownTrigger,
                  cursor: 'default',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ 
                      ...styles.spinner, 
                      width: '14px', 
                      height: '14px',
                      borderTopColor: '#e0eacc',
                      border: '2px solid rgba(224,234,204,0.2)',
                    }}></div>
                    <span style={{ color: '#6b7280' }}>Loading regions...</span>
                  </div>
                </div>
              ) : availableRegions.length > 0 ? (
                <>
                  <div
                    style={{
                      ...styles.dropdownTrigger,
                      borderColor: isRegionDropdownOpen ? '#e0eacc' : '#333',
                    }}
                    onClick={() => !loading && setIsRegionDropdownOpen(!isRegionDropdownOpen)}
                    onMouseEnter={(e) => !loading && (e.currentTarget.style.borderColor = '#e0eacc')}
                    onMouseLeave={(e) => !loading && !isRegionDropdownOpen && (e.currentTarget.style.borderColor = '#333')}
                  >
                    <span style={{ color: selectedRegion ? 'white' : '#6b7280' }}>
                      {getSelectedRegionName()}
                    </span>
                    <span style={{ color: '#6b7280', fontSize: '10px' }}>▼</span>
                  </div>
                  {isRegionDropdownOpen && !loading && (
                    <div style={styles.dropdownMenu} className="firewall-modal-scrollbar">
              {availableRegions.map((region) => (
  <div
    key={region.id}
    style={{
      ...styles.dropdownItem,
      backgroundColor: selectedRegion === region.id ? '#252525' : 'transparent',
    }}
    onClick={() => handleRegionSelect(region.id, region.name)}
    onMouseEnter={(e) => e.target.style.backgroundColor = '#252525'}
    onMouseLeave={(e) => e.target.style.backgroundColor = selectedRegion === region.id ? '#252525' : 'transparent'}
  >
    {sanitizeText(region.id)}  {/* Changed from region.name to region.id */}
  </div>
))}
                    </div>
                  )}
                </>
              ) : (
                <div style={styles.errorBox}>
                  <FaExclamationTriangle style={{ color: '#ef4444', marginTop: '2px' }} />
                  <span style={{ color: '#fca5a5', fontSize: '13px' }}>
                    No regions available. Please contact support.
                  </span>
                </div>
              )}
            </div>

            {/* Description Field */}
            <div style={{ marginBottom: '18px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <label style={styles.label}>Description</label>
                <span style={{ fontSize: '11px', color: descriptionCharCount > 230 ? '#ff6b6b' : '#6b7280' }}>
                  {descriptionCharCount}/255
                </span>
              </div>
              <textarea
                value={description}
                onChange={handleDescriptionChange}
                placeholder="Optional description"
                disabled={loading}
                maxLength={255}
                style={{
                  ...styles.textarea,
                  borderColor: descriptionError ? '#ff6b6b' : '#333',
                  minHeight: isMobileOrTablet ? '80px' : '100px',
                }}
                onFocus={(e) => {
                  if (!loading) {
                    e.target.style.borderColor = '#e0eacc';
                    e.target.style.backgroundColor = '#252525';
                    e.target.style.boxShadow = '0 0 0 3px rgba(224, 234, 204, 0.15)';
                  }
                  logUserAction('firewall_description_field_focused');
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = descriptionError ? '#ff6b6b' : '#333';
                  e.target.style.backgroundColor = '#1a1a1a';
                  e.target.style.boxShadow = 'none';
                  logUserAction('firewall_description_field_blurred', {
                    descriptionLength: description.length,
                    hasError: !!descriptionError
                  });
                }}
              />
              {descriptionError && <div style={styles.errorText}>{sanitizeText(descriptionError)}</div>}
            </div>

            {/* Mobile/Tablet: Summary + Buttons */}
            {isMobileOrTablet && (
              <div style={{ marginTop: '24px' }}>
                {/* Summary Preview */}
                <div style={{
                  padding: '16px',
                  backgroundColor: '#0a0a0a',
                  borderRadius: '10px',
                  marginBottom: '20px',
                }}>
                  <h4 style={{ color: '#6b7280', fontSize: '11px', letterSpacing: '1px', margin: '0 0 12px 0' }}>SUMMARY</h4>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ color: '#6b7280', fontSize: '12px' }}>Name</span>
                    <span style={{ color: 'white', fontSize: '12px' }}>{name || '—'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6b7280', fontSize: '12px' }}>Region</span>
                    <span style={{ color: '#e0eacc', fontSize: '12px' }}>{getSelectedRegionName()}</span>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  onClick={handleSubmit}
                  disabled={loading || fetchingRegions || availableRegions.length === 0 || !isFormValid}
                  style={{
                    ...styles.submitBtn,
                    opacity: (loading || fetchingRegions || availableRegions.length === 0 || !isFormValid) && !loading ? 0.5 : 1,
                  }}
                >
                  {loading ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={styles.spinner}></div>
                      <span>Creating...</span>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FaPlus size={14} />
                      <span>Create Firewall</span>
                    </div>
                  )}
                </button>
                
                <button
                  onClick={handleClose}
                  disabled={loading}
                  style={styles.cancelBtn}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          {/* RIGHT PANEL - Summary (Desktop Only) */}
          {!isMobileOrTablet && (
            <div style={styles.rightPanel}>
              <h3 style={{
                color: '#6b7280',
                fontSize: '11px',
                letterSpacing: '1.5px',
                marginTop: 0,
                marginBottom: '24px',
              }}>SUMMARY</h3>
              
              <div style={{ marginBottom: '20px' }}>
                <p style={styles.summaryLabel}>Firewall Name</p>
                <p style={styles.summaryValue}>{name || '—'}</p>
              </div>
              
           <div style={{ marginBottom: '20px' }}>
  <p style={styles.summaryLabel}>Region</p>
  <p style={{ ...styles.summaryValue, color: '#e0eacc' }}>{selectedRegion || '—'}</p>  {/* This already shows the ID */}
</div>
              
              {description && (
                <div 
                  style={{ marginBottom: '20px', position: 'relative' }}
                  onMouseEnter={() => description.length > 60 && setIsDescriptionHovered(true)}
                  onMouseLeave={() => setIsDescriptionHovered(false)}
                >
                  <p style={styles.summaryLabel}>Description</p>
                  <p style={{ 
                    ...styles.summaryValue, 
                    fontSize: '13px',
                    color: '#9ca3af',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    cursor: description.length > 60 ? 'pointer' : 'default',
                  }}>
                    {description}
                  </p>
                  
                  {/* Hover tooltip for long descriptions */}
                  {isDescriptionHovered && description.length > 60 && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: '-10px',
                      right: '-10px',
                      marginTop: '8px',
                      backgroundColor: '#1a1a1a',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      padding: '12px',
                      zIndex: 200,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                      maxHeight: '120px',
                      overflow: 'auto',
                    }} className="firewall-modal-scrollbar">
                      <p style={{
                        color: '#d1d5db',
                        fontSize: '12px',
                        margin: 0,
                        lineHeight: '1.5',
                        wordBreak: 'break-word',
                      }}>
                        {description}
                      </p>
                    </div>
                  )}
                </div>
              )}
              
              <div style={{ flex: 1 }} />
              
              {/* Submit Button */}
              <button
                onClick={handleSubmit}
                disabled={loading || fetchingRegions || availableRegions.length === 0 || !isFormValid}
                style={{
                  ...styles.submitBtn,
                  opacity: (loading || fetchingRegions || availableRegions.length === 0 || !isFormValid) && !loading ? 0.5 : 1,
                }}
              >
                {loading ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={styles.spinner}></div>
                    <span>Creating...</span>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaPlus size={14} />
                    <span>Create Firewall</span>
                  </div>
                )}
              </button>
              
              <button
                onClick={handleClose}
                disabled={loading}
                style={styles.cancelBtn}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default FirewallCreateModal;